import cn from "clsx";
import { useEffect, useState } from "react";

import { DocumentUsage } from "@/pages/models/release/DataModel/route/Attributes/DocumentUsage";

import { Attribute } from "@/components/AttributeSummaryCard/AttributeSummaryCard";
import Button from "@imminently/decisively-ui/Button";
import Icon from "@imminently/decisively-ui/Icon";
import { Kbd, isMod } from "@imminently/decisively-ui/Kbd";
import Tooltip from "@imminently/decisively-ui/Tooltip";
import { HighlightedText, handlePlainCopy } from "../common/HighlitedText";
import type { SearchResult } from "./Search.types";
import stl from "./SearchAndSelectResultItem.module.scss";

export interface SearchAndSelectResultItemProps {
  result: SearchResult;
  additionalInfo?: {
    entityName?: string;
    type?: string;
    matchingText?: string;
    highlightStr?: string;
  };
  onClear: () => void;
  hideDocumentUsage?: boolean;
  ref?: React.Ref<HTMLDivElement>;
}

const SearchAndSelectResultItem = (props: SearchAndSelectResultItemProps) => {
  const { ref } = props;
  const {
    result: { original },
    additionalInfo,
    onClear,
    hideDocumentUsage = false,
  } = props;

  const [isHovered, setIsHovered] = useState(false);
  const [copied, setCopied] = useState(false);

  // const onDocumentClick = (e: React.MouseEvent, doc: string) => {
  //   props.onDocumentOpen?.();
  // };

  const handleCopy = () => {
    if (additionalInfo?.matchingText) {
      navigator.clipboard.writeText(additionalInfo.matchingText);
      setCopied(true);
    }
  };

  // disabled for now, we might bring it back
  // useHotkey("mod+c", () => {
  //   handleCopy();
  //   onClear();
  //   toast.info("Copied to clipboard");
  // }, isHovered);

  const isRelationship = original.nodeType === "relationship";

  const resultContainer = () => {
    return (
      <div
        className={stl.containerResult}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="flex flex-col gap-2 px-4 py-2">
          <div className={stl.textWithCopy}>
            <Tooltip>
              <Tooltip.Trigger asChild>
                <div
                  onCopy={handlePlainCopy}
                  className={cn(stl.attributeValue, stl.clamp3Lines)}
                >
                  <HighlightedText
                    text={additionalInfo?.matchingText || ""}
                    highlight={additionalInfo?.highlightStr || ""}
                  />
                </div>
              </Tooltip.Trigger>
              <Tooltip.Content>{additionalInfo?.matchingText || ""}</Tooltip.Content>
            </Tooltip>
            {isHovered && (
              <Tooltip
                placement="left"
                onVisibleChange={(open) => !open && setCopied(false)}
              >
                <Tooltip.Trigger asChild>
                  <Button
                    icon
                    size="sm"
                    variant="ghost"
                    className={stl.copyButton}
                    onClick={handleCopy}
                    label="Copy text"
                    tooltip={false}
                  >
                    <Icon name="copy" />
                  </Button>
                </Tooltip.Trigger>
                <Tooltip.Content>{copied ? "Copied!" : "Copy"}</Tooltip.Content>
              </Tooltip>
            )}
          </div>

          {!hideDocumentUsage && (
            <DocumentUsage
              noDuplicates
              id={original.id}
              definedIn={isRelationship ? null : original.definedIn}
              usedIn={isRelationship ? undefined : original.usedIn}
              referencedIn={(original as any).referencedIn}
              onClick={onClear}
            />
          )}
        </div>
        {!isRelationship && <Attribute node={original} />}
      </div>
    );
  };

  return (
    <div
      ref={ref}
      className={stl.container}
    >
      {resultContainer()}
    </div>
  );
};

export default SearchAndSelectResultItem;
